<template>
    <span
        ref="hostEl"
        class="product-image-zoom-host"
        @mouseenter="onZoomEnter"
        @mouseleave="onZoomLeave"
    >
        <template v-if="linkUrl">
            <router-link
                :to="linkUrl"
                class="product-image-link"
                :class="[imageSizeClass, { 'is-broken': showBrokenPlaceholder }]"
            >
                <img
                    v-if="!showBrokenPlaceholder"
                    :src="displaySrc"
                    :class="imageSizeClass"
                    :alt="productName"
                    @error="onImageError"
                />
                <span
                    v-else
                    class="product-image-fallback"
                    :class="imageSizeClass"
                    role="img"
                    :aria-label="productName || 'Image unavailable'"
                ></span>
            </router-link>
        </template>
        <template v-else>
            <div
                class="product-image-container"
                :class="[imageSizeClass, { 'is-broken': showBrokenPlaceholder }]"
            >
                <img
                    v-if="!showBrokenPlaceholder"
                    :src="displaySrc"
                    :class="imageSizeClass"
                    :alt="productName"
                    @error="onImageError"
                />
                <span
                    v-else
                    class="product-image-fallback"
                    :class="imageSizeClass"
                    role="img"
                    :aria-label="productName || 'Image unavailable'"
                ></span>
            </div>
        </template>
    </span>
    <Teleport to="body">
        <Transition name="product-image-zoom">
            <div
                v-if="zoomOpen"
                class="product-image-zoom-preview"
                :style="{
                    left: `${zoomX}px`,
                    top: `${zoomY}px`,
                    '--zoom-from-scale': zoomFromScale,
                }"
                role="presentation"
            >
                <img :src="displaySrc" :alt="productName" />
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch, onUnmounted, nextTick } from 'vue';
import { useMisc } from '@/composables';

const ZOOM_PREVIEW_SIZE = 285;
const ZOOM_VIEWPORT_PAD = 8;

const props = defineProps({
    product: {
        type: Object,
        required: true
    },
    deliveryDate: {
        type: String,
        default: '',
    },
    quoteDate: {
        type: String,
        default: '',
    },
    link: {
        type: Boolean,
        default: true
    },
    zoom: {
        type: Boolean,
        default: false
    },
    imageSize: {
        type: String,
        default: 'sm' 
        /* 
        sm: 44px
        md: 66px
        lg: 177px
        xl: 500px
        */
    }
});

const { getDefaultImage } = useMisc();
const excludedPdpLinkProgramCodes = [
    'dbl', 
    'dbl2', 
    'mmt',
    'sbl'
];

const hostEl = ref(null);
const zoomOpen = ref(false);
const zoomX = ref(0);
const zoomY = ref(0);
const zoomFromScale = ref(0.15);
const imageBroken = ref(false);
const triedDefaultFallback = ref(false);
const overrideSrc = ref('');

const resolvedImageUrl = computed(() => {
    if (props.product.product_image) {
        return props.product.product_image;
    }

    if (props.product.image) {
        return props.product.image;
    }

    return getDefaultImage(props.product.product_type_id) || '';
});

const displaySrc = computed(() => overrideSrc.value || resolvedImageUrl.value);

const showBrokenPlaceholder = computed(
    () => imageBroken.value || !displaySrc.value
);

const canZoom = computed(() => (
    props.zoom && !showBrokenPlaceholder.value && !!displaySrc.value
));

watch(
    () => [props.product?.product_image, props.product?.image, props.product?.product_type_id],
    () => {
        imageBroken.value = false;
        triedDefaultFallback.value = false;
        overrideSrc.value = '';
        closeZoom();
    }
);

const onImageError = () => {
    const defaultImage = getDefaultImage(props.product.product_type_id) || '';
    if (
        !triedDefaultFallback.value &&
        defaultImage &&
        displaySrc.value !== defaultImage
    ) {
        triedDefaultFallback.value = true;
        overrideSrc.value = defaultImage;
        return;
    }
    imageBroken.value = true;
    closeZoom();
};

const productName = computed(() => {
    if (props.product.product_name !== null && props.product.product_name !== '') {
        return props.product.product_name;
    }
    return props.product.name;
});

const linkUrl = computed(() => {
    let programCode = props.product.program_code || props.product.programtype || '';
    programCode = programCode.toLowerCase();
    if (!excludedPdpLinkProgramCodes.includes(programCode) && props.link) {
        if (props.quoteDate) {
            const quoteDate = String(props.quoteDate).split('T')[0].split(' ')[0];
            return '/' + props.product.slug + '?quote_date=' + quoteDate;
        }
        let deliveryDate = '';
        if (props.deliveryDate) {
            deliveryDate = '?'+programCode+'_delivery_date=' + props.deliveryDate;
        }
        return '/' + props.product.slug + deliveryDate;
    }

    return null;
});

const imageSizeClass = computed(() => {
    switch (props.imageSize) {
        case 'square-full':
            return 'product-icon-square-full';
        case 'sm':
            return 'product-icon-sm';
        case 'md':
            return 'product-icon-md';
        case 'lg':
            return 'product-icon-lg';
        case 'xl':
            return 'product-icon-xl';
        case 'xxl':
            return 'product-icon-xxl';
        default:
            return '';
    }
});

const positionZoom = () => {
    const el = hostEl.value;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const size = ZOOM_PREVIEW_SIZE;
    zoomFromScale.value = Math.max(0.08, Math.min(1, rect.width / size));
    let left = rect.left;
    let top = rect.top + rect.height / 2 - size / 2;

    const maxLeft = window.innerWidth - size - ZOOM_VIEWPORT_PAD;
    const maxTop = window.innerHeight - size - ZOOM_VIEWPORT_PAD;
    left = Math.max(ZOOM_VIEWPORT_PAD, Math.min(left, maxLeft));
    top = Math.max(ZOOM_VIEWPORT_PAD, Math.min(top, maxTop));

    zoomX.value = left;
    zoomY.value = top;
};

const closeZoom = () => {
    if (!zoomOpen.value) return;
    zoomOpen.value = false;
    window.removeEventListener('scroll', onZoomScrollOrResize, true);
    window.removeEventListener('resize', onZoomScrollOrResize);
};

const onZoomScrollOrResize = () => {
    if (!zoomOpen.value) return;
    positionZoom();
};

const onZoomEnter = () => {
    if (!canZoom.value) return;
    positionZoom();
    zoomOpen.value = true;
    nextTick(() => {
        if (!zoomOpen.value) return;
        positionZoom();
        window.addEventListener('scroll', onZoomScrollOrResize, true);
        window.addEventListener('resize', onZoomScrollOrResize);
    });
};

const onZoomLeave = () => {
    closeZoom();
};

onUnmounted(() => {
    closeZoom();
});
</script>
<style scoped>
.product-image-link,
.product-image-container {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}
img.product-icon {
    width: 44px;
    height: 44px;
}
.product-icon-sm {
    width: 44px;
    height: 44px;
}
.product-icon-md {
    width: 68px;
    height: 68px;
}
.product-icon-lg {
    width: 177px;
    height: 177px;
}
.product-icon-xl {
    width: 215px;
    height: 215px;
}
.product-icon-xxl {
    width: 500px;
    height: 500px;
}
.product-icon-square-full {
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    outline: none;
    border: none;
    box-shadow: none;
}
.product-image-fallback {
    display: block;
    box-sizing: border-box;
    background-color: var(--secondary-bg, #f5f5f5);
    border: 1px solid var(--border-color, #dee2e6);
}
.product-image-fallback.product-icon-square-full {
    border-radius: var(--br-12, 12px);
}
</style>

<style>
.product-image-zoom-preview {
    position: fixed;
    z-index: 10000;
    width: 285px;
    height: 285px;
    pointer-events: none;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 5px 15px 4px rgb(0 0 0 / 20%);
    transform-origin: left center;
}

.product-image-zoom-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.product-image-zoom-enter-active,
.product-image-zoom-leave-active {
    transition: transform 0.4s ease-in-out, opacity 0.4s ease-in-out, box-shadow 0.4s ease-in-out;
    transform-origin: left center;
}

.product-image-zoom-enter-from,
.product-image-zoom-leave-to {
    transform: scale(var(--zoom-from-scale, 0.15));
    opacity: 0.85;
    box-shadow: none;
}

.product-image-zoom-enter-to,
.product-image-zoom-leave-from {
    transform: scale(1);
    opacity: 1;
}
</style>
